import { checkInteractions } from '../api/medications';

// The members' retry cadence. When some pairs are not cached yet, the backend
// answers 202 and runs the AI checks in the background. On staging those
// finished in 2.6–12 s (median 5.9 s, 21 checks, Aug–Oct 2026), so ask again
// every 2 s at first, then every 5 s. A fixed 10 s made a typical member wait
// 10 s for an answer ready at 6.
//
// Members' route only. The logged-out route counts every request against a
// 50-a-day per-IP ceiling sized on the slower default below, up to 11 requests
// a check (docs/guest-medication-check.md, "Automation guard"); this cadence
// makes up to 26.
export const MEMBER_FAST_RETRY_DELAY_MS = 2000;
export const MEMBER_FAST_RETRIES = 7;
export const MEMBER_SLOW_RETRY_DELAY_MS = 5000;
// 7 × 2 s + 18 × 5 s = 104 s, about the default's 10 × 10 s. The window has to
// outlast the backend's 60 s reset of a pair stuck in 'Processing'.
export const MEMBER_MAX_RETRIES = 25;

export const memberRetryDelayMs = (attempt) =>
  (attempt <= MEMBER_FAST_RETRIES ? MEMBER_FAST_RETRY_DELAY_MS : MEMBER_SLOW_RETRY_DELAY_MS);

// A cancelled check rejects as fetch does when its signal aborts.
const cancelledError = () => {
  const error = new Error('Interaction check cancelled');
  error.name = 'AbortError';
  return error;
};

/**
 * Polls an interaction check until it completes: repeats `request` while it
 * answers `processing_in_progress` (202), up to `maxRetries` times
 * `retryDelayMs` apart. Shared by the members' check and the logged-out
 * visitors' check (STACK-565). `retryDelayMs` may be a function of the attempt
 * number (PR 397): members pass `memberRetryDelayMs`; the logged-out check
 * keeps the 10 s default, which its per-IP ceiling is sized on.
 *
 * When `options.signal` aborts, it asks no more, calls none of the callbacks
 * again, and rejects with an `AbortError`: a member who changes their stack
 * mid-check starts a new check, and the old one's answer is for rows they no
 * longer have.
 *
 * @param {Function} request - `() => Promise<response>`; a response with
 *   `isProcessing` and `status: 'processing_in_progress'` is retried
 * @param {Object} options - as `checkInteractionsWithRetry`'s
 * @returns {Promise<Object>} the completed response
 */
export const pollInteractionCheck = async (request, options = {}) => {
  const {
    retryDelayMs = 10000,
    maxRetries = 10,
    signal,
    onProcessing = () => {},
    onRetry = () => {},
    onComplete = () => {},
    onError = () => {}
  } = options;

  let retryCount = 0;

  const performCheck = async () => {
    try {
      if (signal?.aborted) throw cancelledError();
      const response = await request();
      if (signal?.aborted) throw cancelledError();

      // Handle processing status (202 response)
      if (response.isProcessing && response.status === 'processing_in_progress') {
        onProcessing(response);

        if (retryCount >= maxRetries) {
          const error = new Error(`Maximum retries (${maxRetries}) exceeded while waiting for interaction processing to complete`);
          error.timedOut = true;
          throw error;
        }

        retryCount++;
        const delayMs = typeof retryDelayMs === 'function' ? retryDelayMs(retryCount) : retryDelayMs;

        return new Promise((resolve, reject) => {
          const cancel = () => {
            clearTimeout(timer);
            reject(cancelledError());
          };
          const timer = setTimeout(async () => {
            signal?.removeEventListener('abort', cancel);
            onRetry(retryCount);
            try {
              resolve(await performCheck());
            } catch (error) {
              reject(error);
            }
          }, delayMs);
          signal?.addEventListener('abort', cancel, { once: true });
        });
      }

      onComplete(response);
      return response;

    } catch (error) {
      // Cancelled: not a failure, whatever the request did meanwhile.
      if (signal?.aborted) throw cancelledError();
      // A retry's failure has already been reported by the inner call.
      if (!(error && error.reported)) {
        if (error && typeof error === 'object') error.reported = true;
        console.error('[InteractionChecker] Error during interaction check:', error?.message);
        onError(error);
      }
      throw error;
    }
  };

  return performCheck();
};

/**
 * Utility function to handle interaction checking with automatic retry for processing status
 *
 * @param {Array} medicationIngredients - Array of medication ingredient objects with rxcui data
 * @param {Array} supplementKeys - Array of supplement keys to check
 * @param {string} authToken - Firebase auth token
 * @param {Object} options - Configuration options
 * @param {number|Function} options.retryDelayMs - Delay before each retry in milliseconds, or
 *   `(attempt) => ms` with attempt counting from 1 (default: 10000)
 * @param {number} options.maxRetries - Maximum number of retries (default: 10)
 * @param {AbortSignal} [options.signal] - Cancels the check (see pollInteractionCheck)
 * @param {Function} options.onProcessing - Callback when processing status is received
 * @param {Function} options.onRetry - Callback when retrying
 * @param {Function} options.onComplete - Callback when interactions are complete
 * @param {Function} options.onError - Callback when an error occurs
 * 
 * @returns {Promise<Object>} Final interaction response with completed results
 */
export const checkInteractionsWithRetry = async (
  medicationIngredients, 
  supplementKeys, 
  authToken, 
  options = {}
) => pollInteractionCheck(
  () => checkInteractions(medicationIngredients, supplementKeys, authToken),
  options
);

/**
 * Higher-level utility function for components that need to check interactions
 * and update their state accordingly. Members' route, so it asks again at the
 * members' cadence (memberRetryDelayMs).
 *
 * @param {Array} medicationIngredients - Array of medication ingredient objects
 * @param {Array} supplementKeys - Array of supplement keys
 * @param {string} authToken - Firebase auth token
 * @param {Object} callbacks - State update callbacks
 * @param {Function} callbacks.setIsChecking - Set checking state
 * @param {Function} callbacks.setError - Set error state
 * @param {Function} callbacks.onProcessing - Handle processing state
 * @param {Function} callbacks.onComplete - Handle completion with results
 * @param {AbortSignal} [callbacks.signal] - Cancels the check. Once it aborts,
 *   none of the callbacks is called again, `setIsChecking(false)` included:
 *   the caller has moved on and owns that state. Rejects with an `AbortError`.
 * 
 * @returns {Promise<Object>} Final interaction response
 */
export const checkInteractionsForComponent = async (
  medicationIngredients,
  supplementKeys, 
  authToken,
  callbacks = {}
) => {
  const {
    setIsChecking = () => {},
    setError = () => {},
    onProcessing = () => {},
    onComplete = () => {},
    signal
  } = callbacks;

  let cancelled = false;
  try {
    if (signal?.aborted) throw cancelledError();
    setIsChecking(true);
    setError(null);

    const response = await checkInteractionsWithRetry(
      medicationIngredients,
      supplementKeys,
      authToken,
      {
        retryDelayMs: memberRetryDelayMs,
        maxRetries: MEMBER_MAX_RETRIES,
        signal,
        onProcessing: (processingResponse) => {
          onProcessing(processingResponse);
        },
        onRetry: (attemptNumber) => {
          // Silent retry
        },
        onComplete: (finalResponse) => {
          onComplete(finalResponse);
        },
        onError: (error) => {
          console.error('[InteractionChecker] Check failed:', error);
          setError(error.message || 'Failed to check interactions');
        }
      }
    );

    return response;

  } catch (error) {
    cancelled = Boolean(signal?.aborted);
    if (!cancelled) setError(error.message || 'Failed to check interactions');
    throw error;
  } finally {
    if (!cancelled) setIsChecking(false);
  }
};

/**
 * Utility to extract supplement keys from supplement objects
 * 
 * @param {Array} supplements - Array of supplement objects
 * @returns {Array} Array of supplement keys
 */
export const extractSupplementKeys = (supplements) => {
  if (!supplements || !Array.isArray(supplements)) {
    return [];
  }
  
  return supplements
    .map(supplement => supplement.supplementId || supplement.key)
    .filter(key => key && typeof key === 'string');
};


